import { useEffect, useState } from 'react'
import { connectorsApiService, type Connector, type CreateConnectorRequest } from '../services/connectorsApiService'
import { templatesApiService } from '../services/templatesApiService'
import { integrationsApiService, type OAuthConnection } from '../services/integrationsApiService'
import { ApiError } from '../services/BaseApiService'
import type { Template, TemplateVersion } from '../types'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'
import { Select, useDialogs } from '../components/ui'

const OUTPUT_FORMATS = ['pdf', 'docx', 'xlsx', 'pptx']
const RESPONSE_MODES = ['inline', 'share', 'callback']
const CONNECTOR_TYPES = [
  { value: 'inbound_webhook', label: 'Inbound Webhook' },
  { value: 'salesforce', label: 'Salesforce (outbound pull)' },
  { value: 'hubspot', label: 'HubSpot (outbound pull)' },
]
const VENDOR_TYPES = new Set(['salesforce', 'hubspot'])

const cardStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.lg,
  boxShadow: shadow.sm,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: `${spacing.sm} ${spacing.md}`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.background,
  color: colors.text,
  fontSize: fontSize.base,
  boxSizing: 'border-box',
}

const btnStyle: React.CSSProperties = {
  padding: `${spacing.sm} ${spacing.lg}`,
  borderRadius: radius.sm,
  border: 'none',
  cursor: 'pointer',
  fontSize: fontSize.sm,
  fontWeight: 600,
}

export default function ConnectorsPage() {
  const dialogs = useDialogs()
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, string>>({})

  const [templates, setTemplates] = useState<Template[]>([])
  const [versions, setVersions] = useState<TemplateVersion[]>([])
  const [oauthConnections, setOauthConnections] = useState<OAuthConnection[]>([])
  const [form, setForm] = useState<CreateConnectorRequest>({
    name: '',
    templateVersionId: '',
    fieldMapJson: '{}',
    outputFormat: 'pdf',
    responseMode: 'inline',
    callbackUrl: null,
    connectorType: 'inbound_webhook',
    oAuthConnectionId: null,
  })
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  async function load() {
    setLoading(true)
    try {
      const [cs, ts, oc] = await Promise.all([
        connectorsApiService.list(),
        templatesApiService.list(),
        integrationsApiService.listConnections().catch(() => [] as OAuthConnection[]),
      ])
      setConnectors(cs)
      setTemplates(ts)
      setOauthConnections(oc)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load connectors')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function onTemplateChange(templateId: string) {
    setSelectedTemplateId(templateId)
    setForm(f => ({ ...f, templateVersionId: '' }))
    if (!templateId) { setVersions([]); return }
    try {
      const vs = await templatesApiService.listVersions(templateId)
      setVersions(vs)
      const active = vs.find(v => v.isActive) ?? vs[0]
      if (active) setForm(f => ({ ...f, templateVersionId: active.templateVersionId }))
    } catch { setVersions([]) }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    setCreateError('')
    try {
      const created = await connectorsApiService.create(form)
      setConnectors(prev => [created, ...prev])
      setRevealedSecrets(prev => ({ ...prev, [created.connectorId]: created.secret ?? '' }))
      setShowCreate(false)
      setForm({ name: '', templateVersionId: '', fieldMapJson: '{}', outputFormat: 'pdf', responseMode: 'inline', callbackUrl: null, connectorType: 'inbound_webhook', oAuthConnectionId: null })
      setSelectedTemplateId('')
      setVersions([])
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create connector')
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(connectorId: string) {
    if (!(await dialogs.confirm({ title: 'Delete this connector?', message: 'This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return
    try {
      await connectorsApiService.delete(connectorId)
      setConnectors(prev => prev.filter(c => c.connectorId !== connectorId))
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Delete failed')
    }
  }

  async function handleRotate(connectorId: string) {
    if (!(await dialogs.confirm({ title: 'Rotate the secret?', message: 'The old secret will stop working immediately.', confirmLabel: 'Rotate secret', danger: true }))) return
    try {
      const { secret } = await connectorsApiService.rotateSecret(connectorId)
      setRevealedSecrets(prev => ({ ...prev, [connectorId]: secret }))
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Rotate failed')
    }
  }

  const baseUrl = window.location.origin

  return (
    <>
    <div style={{ padding: spacing.xl, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xl }}>
        <div>
          <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0 }}>Connectors</h1>
          <p style={{ color: colors.textSecondary, marginTop: spacing.xs, fontSize: fontSize.base }}>
            Inbound webhook endpoints that accept JSON and render a template automatically.
          </p>
        </div>
        <button
          style={{ ...btnStyle, background: colors.primary, color: colors.onPrimary }}
          onClick={() => setShowCreate(s => !s)}
        >
          {showCreate ? 'Cancel' : '+ New Connector'}
        </button>
      </div>

      {showCreate && (
        <div style={{ ...cardStyle, marginBottom: spacing.xl }}>
          <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, marginTop: 0, marginBottom: spacing.lg }}>
            New Connector
          </h2>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gap: spacing.md }}>
              <div>
                <label htmlFor="connector-name" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Name</label>
                <input id="connector-name"
                  style={inputStyle}
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Zapier Invoice Hook"
                  required
                />
              </div>

              <div>
                <label htmlFor="connector-connector-type" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Connector type</label>
                <Select id="connector-connector-type"
                  options={CONNECTOR_TYPES}
                  value={form.connectorType ?? 'inbound_webhook'}
                  onChange={v => setForm(f => ({ ...f, connectorType: String(v), oAuthConnectionId: null }))}
                />
              </div>

              {VENDOR_TYPES.has(form.connectorType ?? '') && (
                <div>
                  <label htmlFor="connector-oauth-connection" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>OAuth connection</label>
                  {oauthConnections.length === 0 ? (
                    <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
                      No connections available. <a href="/app/integrations" style={{ color: colors.primary }}>Connect an account</a> first.
                    </div>
                  ) : (
                    <Select id="connector-oauth-connection"
                      placeholder="Select connection…"
                      value={form.oAuthConnectionId ?? ''}
                      onChange={v => setForm(f => ({ ...f, oAuthConnectionId: String(v) || null }))}
                      options={oauthConnections
                        .filter(c => c.provider === (form.connectorType === 'salesforce' ? 'salesforce' : 'hubspot'))
                        .map(c => ({ value: c.oAuthConnectionId, label: c.accountLabel }))}
                    />
                  )}
                </div>
              )}

              <div>
                <label htmlFor="connector-template" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Template</label>
                <Select id="connector-template"
                  placeholder="Select template…"
                  value={selectedTemplateId}
                  onChange={v => onTemplateChange(String(v))}
                  options={templates.map(t => ({ value: t.templateId, label: t.name }))}
                />
              </div>

              {versions.length > 0 && (
                <div>
                  <label htmlFor="connector-version" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Version</label>
                  <Select id="connector-version"
                    value={form.templateVersionId}
                    onChange={v => setForm(f => ({ ...f, templateVersionId: String(v) }))}
                    options={versions.map(v => ({ value: v.templateVersionId, label: `v${v.versionNumber}${v.isActive ? ' (active)' : ''}` }))}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <div>
                  <label htmlFor="connector-output-format" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Output format</label>
                  <Select id="connector-output-format"
                    value={form.outputFormat}
                    onChange={v => setForm(f => ({ ...f, outputFormat: String(v) }))}
                    options={OUTPUT_FORMATS.map(f => ({ value: f, label: f.toUpperCase() }))}
                  />
                </div>
                <div>
                  <label htmlFor="connector-response-mode" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Response mode</label>
                  <Select id="connector-response-mode"
                    value={form.responseMode}
                    onChange={v => setForm(f => ({ ...f, responseMode: String(v) }))}
                    options={RESPONSE_MODES.map(m => ({ value: m, label: m }))}
                  />
                </div>
              </div>

              {form.responseMode === 'callback' && (
                <div>
                  <label htmlFor="connector-callback-url" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Callback URL</label>
                  <input id="connector-callback-url"
                    style={inputStyle}
                    type="url"
                    value={form.callbackUrl ?? ''}
                    onChange={e => setForm(f => ({ ...f, callbackUrl: e.target.value || null }))}
                    placeholder="https://…"
                  />
                </div>
              )}

              <div>
                <label htmlFor="connector-field-map-json" style={{ display: 'block', fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>
                  Field map JSON <span style={{ color: colors.textSecondary, fontWeight: 400 }}>(optional — maps incoming field names to template variables)</span>
                </label>
                <textarea id="connector-field-map-json"
                  style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 80, resize: 'vertical' }}
                  value={form.fieldMapJson}
                  onChange={e => setForm(f => ({ ...f, fieldMapJson: e.target.value }))}
                />
              </div>

              {createError && <div style={{ color: colors.error, fontSize: fontSize.sm }}>{createError}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={creating || !form.name || !form.templateVersionId}
                  style={{ ...btnStyle, background: colors.primary, color: colors.onPrimary, opacity: creating ? 0.6 : 1 }}
                >
                  {creating ? 'Creating…' : 'Create Connector'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {error && <div style={{ color: colors.error, marginBottom: spacing.md }}>{error}</div>}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading…</div>
      ) : connectors.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: colors.textSecondary, padding: spacing.xl }}>
          No connectors yet. Create one to expose a webhook endpoint.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: spacing.md }}>
          {connectors.map(c => {
            const expanded = expandedId === c.connectorId
            const secret = revealedSecrets[c.connectorId]
            const receiveUrl = `${baseUrl}${c.receiveUrl}`
            return (
              <div key={c.connectorId} style={cardStyle}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setExpandedId(expanded ? null : c.connectorId)}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.base }}>{c.name}</div>
                    <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 2 }}>
                      {c.outputFormat.toUpperCase()} · {c.responseMode} · {c.isActive ? 'active' : 'inactive'}
                      {c.lastTriggeredAt && ` · last triggered ${new Date(c.lastTriggeredAt).toLocaleString()}`}
                    </div>
                  </div>
                  <span style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>{expanded ? '▲' : '▼'}</span>
                </div>

                {expanded && (
                  <div style={{ marginTop: spacing.md, display: 'grid', gap: spacing.md }}>
                    <div>
                      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Endpoint URL</div>
                      <code style={{ display: 'block', background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, fontSize: fontSize.sm, wordBreak: 'break-all' }}>
                        POST {receiveUrl}
                      </code>
                      <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Set <code>Authorization: Bearer &lt;secret&gt;</code> header with each request.
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Secret</div>
                      {secret ? (
                        <code style={{ display: 'block', background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, fontSize: fontSize.sm, wordBreak: 'break-all' }}>
                          {secret}
                        </code>
                      ) : (
                        <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Hidden — rotate to reveal a new secret.</div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>Field map</div>
                      <pre style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, fontSize: fontSize.sm, margin: 0, overflowX: 'auto' }}>
                        {c.fieldMapJson}
                      </pre>
                    </div>

                    <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                      <button
                        style={{ ...btnStyle, background: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
                        onClick={() => handleRotate(c.connectorId)}
                      >
                        Rotate Secret
                      </button>
                      <button
                        style={{ ...btnStyle, background: colors.surface, color: colors.error, border: `1px solid ${colors.error}` }}
                        onClick={() => handleDelete(c.connectorId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
      {dialogs.element}
    </>
  )
}
